import React from 'react'
import { Form, Modal, Input, Typography } from '@pankod/refine-antd'

import { MaskedTextInput } from '@components/MaskedTextInput'

const { Text } = Typography

export type TSignFormValues = {
  'bsre-nik': string
  'bsre-phrase': string
}

// --- Approve Modal ---
interface TApproveModalProps {
  open: boolean
  namaDiklat?: string
  isSubmitting: boolean
  onClose: () => void
  onConfirm: () => void
}

export const ApproveModal: React.FC<TApproveModalProps> = ({
  open,
  namaDiklat,
  isSubmitting,
  onClose,
  onConfirm,
}) => (
  <Modal
    open={open}
    title="Konfirmasi Persetujuan"
    okText="Ya, Setujui"
    cancelText="Batal"
    confirmLoading={isSubmitting}
    okButtonProps={{ type: 'primary', disabled: isSubmitting }}
    cancelButtonProps={{ disabled: isSubmitting }}
    closable={!isSubmitting}
    maskClosable={!isSubmitting}
    keyboard={!isSubmitting}
    onOk={() => {
      if (!isSubmitting) onConfirm()
    }}
    onCancel={() => {
      if (!isSubmitting) onClose()
    }}
    destroyOnClose
  >
    <p>
      Apakah Anda yakin ingin menyetujui pengajuan penerbitan sertifikat untuk{' '}
      <strong>{namaDiklat}</strong>?
    </p>
  </Modal>
)

// --- Sign Modal ---
interface TSignModalProps {
  open: boolean
  namaDiklat?: string
  isSubmitting: boolean
  onClose: () => void
  onConfirm: (
    values: TSignFormValues,
    setFieldError: (name: string, error: string) => void,
  ) => void
}

export const SignModal: React.FC<TSignModalProps> = ({
  open,
  namaDiklat,
  isSubmitting,
  onClose,
  onConfirm,
}) => {
  const [form] = Form.useForm()

  return (
    <Modal
      open={open}
      title="Konfirmasi Tanda Tangan"
      okText="Ya, Tandatangani"
      cancelText="Batal"
      confirmLoading={isSubmitting}
      okButtonProps={{ type: 'primary', disabled: isSubmitting }}
      cancelButtonProps={{ disabled: isSubmitting }}
      closable={!isSubmitting}
      maskClosable={!isSubmitting}
      keyboard={!isSubmitting}
      onOk={() => {
        if (!isSubmitting) form.submit()
      }}
      onCancel={() => {
        if (isSubmitting) return
        form.resetFields()
        onClose()
      }}
      afterClose={() => form.resetFields()}
      destroyOnClose
    >
      <p>
        Apakah Anda yakin ingin menandatangani sertifikat untuk{' '}
        <strong>{namaDiklat}</strong>? Tanda tangan elektronik akan diajukan ke
        BSrE.
      </p>
      <Form
        form={form}
        layout="vertical"
        disabled={isSubmitting}
        onFinish={(values) => {
          onConfirm(values, (name, error) => {
            form.setFields([{ name, errors: [error] }])
          })
        }}
        style={{ marginTop: 16 }}
      >
        <Form.Item
          label={<Text strong>NIK</Text>}
          name="bsre-nik"
          rules={[
            { required: true, message: 'NIK wajib diisi' },
            {
              len: 16,
              message: 'NIK harus berjumlah tepat 16 digit',
            },
            {
              pattern: /^[0-9]+$/,
              message: 'NIK hanya boleh berisi angka',
            },
          ]}
        >
          <Input
            placeholder="Masukkan NIK"
            maxLength={16}
            disabled={isSubmitting}
            autoComplete="off"
            autoCorrect="off"
            id="bsre-nik"
            name="bsre-nik"
          />
        </Form.Item>
        <Form.Item
          label={<Text strong>Passphrase</Text>}
          name="bsre-phrase"
          rules={[{ required: true, message: 'Passphrase wajib diisi' }]}
        >
          <MaskedTextInput
            placeholder="Masukkan Passphrase"
            disabled={isSubmitting}
            id="bsre-phrase"
            name="bsre-phrase"
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}

// --- Reject Modal ---
interface TRejectModalProps {
  open: boolean
  namaDiklat?: string
  isSubmitting: boolean
  rejectReason: string
  setRejectReason: (val: string) => void
  onClose: () => void
  onConfirm: () => void
}

export const RejectModal: React.FC<TRejectModalProps> = ({
  open,
  namaDiklat,
  isSubmitting,
  rejectReason,
  setRejectReason,
  onClose,
  onConfirm,
}) => (
  <Modal
    open={open}
    title="Konfirmasi Penolakan"
    okText="Ya, Tolak"
    cancelText="Batal"
    confirmLoading={isSubmitting}
    okButtonProps={{
      danger: true,
      disabled: isSubmitting || rejectReason.trim() === '',
    }}
    cancelButtonProps={{ disabled: isSubmitting }}
    closable={!isSubmitting}
    maskClosable={!isSubmitting}
    keyboard={!isSubmitting}
    onOk={() => {
      if (!isSubmitting) onConfirm()
    }}
    onCancel={() => {
      if (!isSubmitting) onClose()
    }}
    destroyOnClose
  >
    <p>
      Anda akan menolak pengajuan sertifikat untuk <strong>{namaDiklat}</strong>
      .
    </p>
    <div style={{ marginTop: 12 }}>
      <div style={{ marginBottom: 4 }}>
        <Text strong>
          Alasan Penolakan <Text type="danger">*</Text>
        </Text>
      </div>
      <Input.TextArea
        rows={4}
        placeholder="Masukkan alasan penolakan..."
        value={rejectReason}
        disabled={isSubmitting}
        onChange={(e) => setRejectReason(e.target.value)}
      />
    </div>
  </Modal>
)
